import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BmarkClientOrdersBlotterMode } from "./enums/BmarkClientOrdersBlotterMode.enum";
import { Serializer } from "shared/provider-webworker";
import { BmarkRiskData } from "shared/cubiq-services/model/benchmarks/BmarkRiskData.model";
import { BMRiskUtils, RiskData } from "shared/cubiq-services/model/benchmarks/helpers/BMRiskUtils";
import { BMOrderAmountHelper } from "shared/cubiq-services/model/benchmarks/helpers/BMOrderAmountHelper";
import { ValueDateHelper } from "shared/cubiq-services/model/orders/ValueDateHelper";

const MAX_TOOLTIP_SIZE = 512;

export class BmarkOrderData extends CubiqTS.OrderDataImpl {
  protected static _serializer: Serializer = new CubiqTS.CubiqSerializer();

  public traderGroupNames: string;
  public spawned: boolean;
  private _blotterMode: BmarkClientOrdersBlotterMode;
  public primaryKey: string;

  public executedPerc: number;

  private _risk: BmarkRiskData;

  constructor(public source: BmarkOrderData) {
    super();
    this._blotterMode = BmarkClientOrdersBlotterMode.DEALT;
    this.copyFrom(source);
    this.calculateExecutedPercentage();
  }

  protected calculateExecutedPercentage() {
    let filled = this.executedQty;

    if (this.leavesQty != null && this.orderQty != null) {
      this.orderQty.minus(this.leavesQty);
    }

    if (filled != null && this.orderQty != null && !this.orderQty.isZero()) {
      this.executedPerc = Number(filled.dividedBy(this.orderQty)) * 100;
    }
  }

  public copyFrom(source: BmarkOrderData) {
    if (source != null) {
      Object.keys(source).forEach((key) => (this[key] = source[key]));
    }
  }

  public static clone(value: BmarkOrderData | CubiqTS.OrderDataImpl): BmarkOrderData {
    let ret: BmarkOrderData = new BmarkOrderData(value as BmarkOrderData);
    if (value.allocationData != null && value.allocationData.length > 0) {
      ret.allocationData = value.allocationData.slice();
    }
    return ret;
  }

  public set blotterMode(value: BmarkClientOrdersBlotterMode) {
    if (value !== this._blotterMode) {
      this._risk = null;
      this._blotterMode = value;
    }
  }

  public get blotterMode(): BmarkClientOrdersBlotterMode {
    return this._blotterMode;
  }

  public get risk(): BmarkRiskData {
    if (this._risk == null) {
      this._risk = this.partitionAllocationsForRisk();
    }
    return this._risk;
  }

  /**
   * Is this a multi-allocation risk?
   */
  public get multiAllocation(): boolean {
    const risk = this.risk;
    return risk?.allocations?.length > 1;
  }

  /**
   * For multi-allocation benchmarks, list all the allocations inside the order. Uses sililar formatting to tooltip used
   * in Scratchpad (in ScratchPadOrderFactoryService)
   *
   * @param orderAmountHelper Formats amounts based on ccy model
   * @param valueDateHelper Formats tenors and value dates
   * @returns
   */
  public createProductTooltip(
    orderAmountHelper: BMOrderAmountHelper,
    valueDateHelper: ValueDateHelper,
  ): string {
    const allocations = this.risk?.allocations;
    if (allocations?.length > 1) {
      const rows: string[] = ["Allocations:"];
      allocations.forEach((allocation) => {
        const intentStr = allocation.allocationIntent == CubiqTS.OrderIntent.BUY ? "BUY" : "SELL";
        const amountStr = orderAmountHelper.formatAmount(
          this.risk.fixedCcy,
          new CubiqTS.BigNumber(allocation.allocationQty),
          false,
          null,
          null,
        );
        let valueDateStr: string;
        const settleDate = allocation.settleDate;
        valueDateStr = valueDateHelper.formatValueDate(settleDate, allocation.settleTenor);

        rows.push(`${intentStr} ${amountStr} ${valueDateStr} Account: ${allocation.accountCode}`);
      });
      // Make sure our tooltip is less than the max size
      const fullText = rows.join("\n");
      if (fullText.length > MAX_TOOLTIP_SIZE) {
        return fullText.substring(1, MAX_TOOLTIP_SIZE) + "...";
      }
      return fullText;
    }
    return "";
  }

  /**
   * Unlike OrderDataImpl, the allocations of a BmarkOrderData are assigned on creation based on
   * the view that is being used.
   */
  private partitionAllocationsForRisk(): BmarkRiskData {
    if (this.orderType === CubiqTS.OrderType.BMALGO) {
      return new BmarkRiskData(this, this.allocationData);
    }
    const riskMap = BMRiskUtils.createRiskMap(this.allocationData);
    let retRiskData: RiskData = null;
    const values = riskMap.values();

    // We can have 1 or 3 risks - if, 1 then this is the only risk for
    // the order (may be component if only created with component allocations)
    if (riskMap.size == 1) {
      retRiskData = riskMap.values().next().value;
    } else {
      for (const value of values) {
        if (this._blotterMode == BmarkClientOrdersBlotterMode.DEALT && value.isDealt) {
          retRiskData = value;
          break;
        } else if (this._blotterMode == BmarkClientOrdersBlotterMode.RISK && !value.isDealt) {
          retRiskData = value;
          break;
        }
      }
    }
    if (retRiskData) {
      return new BmarkRiskData(this, retRiskData.allocations);
    }
    return null;
  }
}
